import React, { useState } from 'react'
import { Form, Button, Modal, Alert, Card, Spinner } from 'react-bootstrap'
import useFetch from '../../_fetch'
import { useAuth } from '../../auth/auth-context'

export default function PasswordSection(opts: { setSuccessModalMsg: (value: React.SetStateAction<string>) => void }) {
    const { doFetch } = useFetch()
    const { login } = useAuth()
    const [showModal, setShowModal] = useState(false)
    const [submitting, setSubmitting] = useState(false) // State to track form submission
    const [passwordFields, setPasswordFields] = useState({
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    })
    const [passwordError, setPasswordError] = useState('')

    const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setPasswordFields((prevFields) => ({
            ...prevFields,
            [name]: value
        }))
    }

    const handlePasswordUpdate = async (e: React.FormEvent) => {
        e.preventDefault()
        if (passwordFields.newPassword !== passwordFields.confirmNewPassword) {
            setPasswordError('Passwords do not match')
        } else {
            setPasswordError('')
            setSubmitting(true) 
            
            try {
                const res = await doFetch('/update-password', {
                    oldPassword: passwordFields.oldPassword,
                    newPassword: passwordFields.newPassword
                })

                if (res.status >= 400) {
                    setPasswordError(await res.text())
                } else {
                    const { token } = await res.json()
                    login(token)
                    setShowModal(false)
                    setPasswordFields({
                        oldPassword: '',
                        newPassword: '',
                        confirmNewPassword: ''
                    })
                    opts.setSuccessModalMsg('Password updated successfully')
                }
            } catch (error) {
                setPasswordError('Failed to update password')
            } finally {
                setSubmitting(false) 
            }
        }
    }

    const handleCloseModal = () => {
        setPasswordError('')
        setShowModal(false)
        setPasswordFields({
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: ''
        })
    }

    return (
        <>
            <Card className="mb-3">
                <Card.Header>
                    <div className="fw-semibold">Password Settings</div>
                    <div className="text-muted small">Manage account security credentials.</div>
                </Card.Header>
                <Card.Body>
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                        <div className="text-muted small">Use a strong password with at least 8 characters.</div>
                        <Button variant="outline-primary" onClick={() => setShowModal(true)}>
                            Change password
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Change Password</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePasswordUpdate}>
                    <Modal.Body>
                        <Form.Group controlId="formOldPassword">
                            <Form.Label>Old Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Enter old password"
                                name="oldPassword"
                                value={passwordFields.oldPassword}
                                onChange={handlePasswordInputChange}
                                required
                                minLength={8}
                            />
                        </Form.Group>
                        <Form.Group controlId="formNewPassword">
                            <Form.Label>New Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Enter new password"
                                name="newPassword"
                                value={passwordFields.newPassword}
                                onChange={handlePasswordInputChange}
                                required
                                minLength={8}
                            />
                        </Form.Group>
                        <Form.Group controlId="formConfirmNewPassword">
                            <Form.Label>Confirm New Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Confirm new password"
                                name="confirmNewPassword"
                                value={passwordFields.confirmNewPassword}
                                onChange={handlePasswordInputChange}
                                required
                                minLength={8}
                            />
                            {passwordError && <Alert className="mt-3" variant="danger">{passwordError}</Alert>}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    )
}
